import { useCallback } from 'react';
import { buildCRMContext } from '../services/aiChat';
import { useCRM } from '../contexts/CRMContext';
import { useEstimates } from '../contexts/EstimatesContext';
import { useProjects } from '../contexts/ProjectsContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigation } from '../contexts/NavigationContext';
import { useSettings } from '../contexts/SettingsContext';
import { useFleet } from '../contexts/FleetContext';
import { useTimeClockCtx } from '../contexts/TimeClockContext';
import { useAIChat } from '../contexts/AIChatContext';
import { useContracts } from './useServiceQueries';

/** Builds the CRM payload for AI chat — shared by panel, full page, and contextual Ask Raza. */
export function useAIChatCRMContext(extraFocus?: string) {
  const { contextualFocus } = useAIChat();
  const { leads, clients, appointments } = useCRM();
  const { estimates, pricebook, serviceTemplates } = useEstimates();
  const { projects } = useProjects();
  const { currentUser, userRole } = useAuth();
  const { currentView } = useNavigation();
  const { settings, metrics } = useSettings();
  const { equipment } = useFleet();
  const { crews, teamMembers, activeTimeLogs } = useTimeClockCtx();
  // Recurring service work. Without this the AI is handed an empty contract
  // list and confidently reports "0 active contracts".
  const { data: serviceContracts = [] } = useContracts();

  return useCallback(
    () =>
      buildCRMContext({
        userName: currentUser?.displayName || 'User',
        // Least privilege on an unresolved role. Defaulting to 'Admin' told the
        // model it was talking to an owner before auth had finished loading.
        userRole: userRole || 'Laborer',
        currentView,
        settings,
        metrics,
        clients,
        estimates,
        projects,
        leads,
        appointments,
        pricebook,
        serviceTemplates,
        equipment,
        crews,
        teamMembers,
        activeTimeLogs,
        serviceContracts,
        contextualFocus: extraFocus || contextualFocus || undefined,
      }),
    [
      extraFocus,
      contextualFocus,
      currentUser?.displayName,
      userRole,
      currentView,
      settings,
      metrics,
      clients,
      estimates,
      projects,
      leads,
      appointments,
      pricebook,
      serviceTemplates,
      equipment,
      crews,
      teamMembers,
      activeTimeLogs,
      serviceContracts,
    ]
  );
}
